import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import {
  LAST_SEARCHS_KEY,
  LAST_SEARCHS_MAX_LENGTH,
  RECOMMENDED_FLATS_MAX_LENGTH,
} from '../consts';
import { shuffle } from '../helpers/array.utils';
import { getLastSearchs } from '../helpers/lastSearchs.utils';
import * as searchServiceUtils from '../helpers/searchService.utils';
import { IFlat } from '../model/flat.model';
import { IMapArea } from '../model/mapArea.model';
import { FlatOrderBy } from '../model/enums/flatOrderBy.enum';

export type SearchService = {
  getResults: () => IFlat[];
  getResultsCount: () => number;
  getPageSize: () => number;
  incrementPageSize: (maximum?: boolean) => void;
  getOrderBy: () => FlatOrderBy;
  setOrderBy: (orderBy: FlatOrderBy) => void;
  searchInMap: (mapBounds: google.maps.LatLngBounds) => void;
  getFilter: () => SearchFilter;
  getLastSearchsResults: () => IFlat[];
};

export type SearchFilter = {
  county?: string;
  city?: string;
  zone?: string;
  q?: string;
  types?: string[];
  priceMin?: number;
  priceMax?: number;
  sqrMetersMin?: number;
  sqrMetersMax?: number;
  rooms?: number[];
  roomsMin?: number;
  bathrooms?: number[];
  bathroomsMin?: number;
  characteristics?: string[];
  mapArea?: IMapArea;
  mapBounds?: google.maps.LatLngBounds;
};

const INITIAL_PAGE_SIZE = 10;
const PAGE_SIZE_INCREMENT_SIZE = 10;
const INITIAL_ORDER_BY = FlatOrderBy.PRICE_ASC;

const useSearchService = (
  flats: IFlat[],
  originalResults?: IFlat[],
  originalMapArea?: IMapArea
): SearchService => {
  const router = useRouter();

  const [results, setResults] = useState(originalResults || []);
  const [pageSize, setPageSize] = useState(INITIAL_PAGE_SIZE);

  const slicedResults = useMemo(() => results.slice(0, pageSize), [
    pageSize,
    results,
  ]);

  const orderByRef = useRef(INITIAL_ORDER_BY);
  const firstRenderRef = useRef(true);
  const flatsRef = useRef(flats);
  const mapAreaRef = useRef(originalMapArea);
  const mapBoundsRef = useRef<google.maps.LatLngBounds | undefined>();
  const routerQueryRef = useRef(router.query);

  const computeFilter = useCallback((): SearchFilter => {
    const filter = searchServiceUtils.generateFilterFromQuery(
      routerQueryRef.current
    );
    if (filter.county || filter.city || filter.zone) {
      mapAreaRef.current = undefined;
    }
    return {
      ...filter,
      mapArea: mapAreaRef.current,
      mapBounds: mapBoundsRef.current,
    };
  }, []);

  const [filter, setFilter] = useState<SearchFilter>(computeFilter());

  const getResults = useCallback(() => slicedResults, [slicedResults]);

  const getPageSize = useCallback(() => Math.min(results.length, pageSize), [
    pageSize,
    results.length,
  ]);

  const getResultsCount = useCallback(() => results.length, [results]);

  const incrementPageSize = useCallback(
    (maximum = false) => {
      setPageSize(
        maximum
          ? results.length
          : Math.min(pageSize + PAGE_SIZE_INCREMENT_SIZE, results.length)
      );
    },
    [pageSize, results.length]
  );

  const setOrderBy = useCallback(
    (orderBy: FlatOrderBy) => {
      orderByRef.current = orderBy;
      const resultsToSort = [...results];
      searchServiceUtils.sortResults(resultsToSort, orderByRef.current);
      setResults(resultsToSort);
    },
    [results]
  );

  const getLastSearchsResults = useCallback((): IFlat[] => {
    const lastSearchs = getLastSearchs();
    let lastSearchsResults = lastSearchs
      .flatMap((search) =>
        searchServiceUtils.computeResults(flatsRef.current, search)
      )
      .slice(0, RECOMMENDED_FLATS_MAX_LENGTH);
    lastSearchsResults = shuffle(lastSearchsResults);

    lastSearchsResults.push(
      ...shuffle(
        flatsRef.current
          .filter((flat) => !lastSearchsResults.some((f) => flat.id === f.id))
          .slice(0, RECOMMENDED_FLATS_MAX_LENGTH - lastSearchsResults.length)
      )
    );

    return lastSearchsResults;
  }, []);

  const computeResults = useCallback((): IFlat[] => {
    const newFilter = computeFilter();
    setFilter(newFilter);

    const newResults = searchServiceUtils.computeResults(
      flatsRef.current,
      newFilter,
      orderByRef.current
    );

    if (
      newResults.length > 0 &&
      Object.entries(newFilter).some((e) => !!e[1])
    ) {
      const lastSearchs = getLastSearchs();
      const base64 = btoa(JSON.stringify(newFilter));
      if (!lastSearchs.some((search) => search.base64 === base64)) {
        const newLastSearchs = [
          {
            base64: btoa(JSON.stringify(newFilter)),
            ...newFilter,
          },
          ...lastSearchs.slice(0, LAST_SEARCHS_MAX_LENGTH - 1),
        ];
        localStorage.setItem(LAST_SEARCHS_KEY, JSON.stringify(newLastSearchs));
      }
    }

    return newResults;
  }, [computeFilter]);

  const searchInMap = useCallback(
    (mapBounds: google.maps.LatLngBounds) => {
      mapBoundsRef.current = mapBounds;
      setResults(computeResults());
    },
    [computeResults]
  );

  useEffect(() => {
    flatsRef.current = flats;
  }, [flats]);

  useEffect(() => {
    setFilter(computeFilter());
  }, [computeFilter]);

  useEffect(() => {
    if (routerQueryRef && routerQueryRef.current === router.query) {
      return;
    }

    routerQueryRef.current = router.query;

    if (globalThis.window === undefined) {
      return;
    }

    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      if (!router.asPath.includes('?')) {
        return;
      }
    }

    setResults(computeResults());
  }, [computeResults, router.asPath, router.query]);

  return {
    getResults,
    getResultsCount,
    getPageSize,
    incrementPageSize,
    getOrderBy: () => orderByRef.current,
    setOrderBy,
    searchInMap,
    getFilter: () => filter,
    getLastSearchsResults,
  };
};

export default useSearchService;
